// Modal de criar/editar documento da empresa.
// Refeito em 2026-05-25 pra usar API nova /comercial/documentos com:
//   - Upload presigned URL (PUT direto no S3, aguenta arquivos grandes)
//   - Sumarização síncrona com Haiku no criar (espera 5-15s)
//   - Schema novo (categoria_id + status calculado + sumario_md)
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Input, Select, Modal, Form, Typography, Row, Col, App as AntApp,
  DatePicker, Button, Upload, Tag, Alert, Spin, Divider, Space, Popconfirm,
  Switch, Radio,
} from 'antd';
import { UploadOutlined, FileTextOutlined, LinkOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { Documento, CategoriaDoc, SubcategoriaDoc, DocumentoVinculado, TipoVinculo } from './types';

const { Text } = Typography;

const TIPO_VINCULO_LABEL: Record<string, string> = {
  'atestado-cat': 'Atestado ↔ CAT',
  'renovacao':    'Renovação (substitui anterior)',
  'aditivo':      'Aditivo',
  'versao':       'Versão posterior',
  'outro':        'Outro',
};

export function ModalDocumento({ documento, categorias, subcategorias, todosDocs, onClose, onSaved, iniciarTroca, somenteLeitura, paraEsperado }: {
  documento: Documento | null;
  categorias: CategoriaDoc[];
  subcategorias: SubcategoriaDoc[];
  todosDocs: Documento[];
  onClose: () => void;
  onSaved: () => void;
  /** Veio do botão "Anexar" de uma linha do checklist: categoria e subcategoria
   *  já vêm resolvidas, e o documento nasce AMARRADO àquele item (esperado_id).
   *  O vínculo explícito é o que faz um item de instância — o CRLV de UM
   *  veículo — fechar; o casamento automático não alcança esse caso. */
  paraEsperado?: { id: number; nome: string; categoria_codigo: string; subcategoria_codigo?: string | null };
  // L10 (2026-06-02): permite abrir ja com o modal de troca aberto
  // (quando o usuario clicou no botao 'Trocar arquivo' direto na linha da tabela).
  iniciarTroca?: boolean;
  /** Consulta do Comercial: campos desabilitados, sem trocar arquivo, sem
   *  vincular. O backend recusaria de qualquer forma — aqui é só não oferecer. */
  somenteLeitura?: boolean;
}) {
  const { message } = AntApp.useApp();
  const editando = !!documento;
  const [form, setForm] = useState({
    categoria_codigo: documento?.categoria_codigo || paraEsperado?.categoria_codigo || (categorias[0]?.codigo ?? ''),
    subcategoria_codigo: documento?.subcategoria_codigo || paraEsperado?.subcategoria_codigo || '',
    titulo: documento?.titulo || '',
    descricao: documento?.descricao || '',
    emitente: documento?.emitente || '',
    numero_documento: documento?.numero_documento || '',
    data_emissao: documento?.data_emissao?.slice(0, 10) || '',
    data_validade: documento?.data_validade?.slice(0, 10) || '',
  });
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [sumarioMd, setSumarioMd] = useState<string | null>(documento?.sumario_md || null);
  const [progressoMsg, setProgressoMsg] = useState('');
  const [vinculados, setVinculados] = useState<DocumentoVinculado[]>(documento?.vinculados || []);
  const [novoVincDocId, setNovoVincDocId] = useState<number | null>(null);
  const [novoVincTipo, setNovoVincTipo] = useState<TipoVinculo>('atestado-cat');
  const [vincLoading, setVincLoading] = useState(false);
  // F8 — modelo editavel pela IA (sem placeholders)
  const [ehTemplate, setEhTemplate] = useState<boolean>(documento?.eh_template === true);
  // Visibilidade ao Comercial: TRÊS estados, não dois.
  //   null  = herda da categoria (default, e o que 100% dos documentos são hoje)
  //   true  = mostra, mesmo que a categoria esconda
  //   false = esconde, mesmo que a categoria mostre
  // `undefined` no state seria um quarto estado sem significado, então
  // normaliza para null já na entrada.
  const [visComercial, setVisComercial] = useState<boolean | null>(
    typeof documento?.visivel_comercial_override === 'boolean'
      ? documento.visivel_comercial_override : null);
  // L10 (2026-06-02) — Modal pra trocar o arquivo do doc existente.
  const [trocarOpen, setTrocarOpen] = useState(!!iniciarTroca);
  const [trocaArquivo, setTrocaArquivo] = useState<File | null>(null);
  const [trocaDataEmissao, setTrocaDataEmissao] = useState<string>('');
  const [trocaDataValidade, setTrocaDataValidade] = useState<string>('');
  const [trocando, setTrocando] = useState(false);

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  // Ao editar, refetch do doc COMPLETO. A listagem é enxuta de propósito
  // (`SELECT` sem `sumario_md` nem `eh_template`, para não trafegar o acervo
  // inteiro), e o modal precisa dos dois:
  //
  //  - `eh_template` vinha `undefined` da listagem, virava `false` no estado e
  //    era reenviado como `eh_template: false` em TODA edição — salvar qualquer
  //    campo de um modelo editável apagava a marca de modelo. (Revisão de
  //    19/08; em produção só existe 1 modelo e ele está intacto.)
  //  - `sumario_md` também só existe no `get`: o sumário da IA aparecia nos
  //    segundos após a criação e depois sumia para sempre ao reabrir.
  const recarregarVinculos = useCallback(async () => {
    if (!documento) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'get', id: documento.id },
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const completo = data.documento;
      setVinculados(completo?.vinculados || []);
      if (completo) {
        setEhTemplate(completo.eh_template === true);
        // Mesma armadilha do eh_template (comentada acima): o valor vem da
        // listagem, mas quem manda é o `get`.
        setVisComercial(typeof completo.visivel_comercial_override === 'boolean'
          ? completo.visivel_comercial_override : null);
        if (completo.sumario_md) setSumarioMd(completo.sumario_md);
      }
    } catch { /* silent */ }
  }, [documento]);
  useEffect(() => { if (editando) recarregarVinculos(); }, [editando, recarregarVinculos]);

  async function adicionarVinculo() {
    if (!documento || !novoVincDocId) { message.warning('Selecione um documento para vincular'); return; }
    setVincLoading(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'vincular', doc_a_id: documento.id, doc_b_id: novoVincDocId, tipo_vinculo: novoVincTipo },
        { headers: { Authorization: token } });
      setNovoVincDocId(null);
      await recarregarVinculos();
      message.success('Vinculado.');
    } catch (e: any) {
      const det = e.response?.data?.error || e.message;
      message.error('Erro ao vincular: ' + det);
    } finally { setVincLoading(false); }
  }

  async function removerVinculo(vincId: number) {
    if (!documento) return;
    setVincLoading(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'desvincular', doc_a_id: documento.id, doc_b_id: vincId },
        { headers: { Authorization: token } });
      await recarregarVinculos();
      message.success('Vínculo removido.');
    } catch (e: any) {
      message.error('Erro ao remover: ' + (e.response?.data?.error || e.message));
    } finally { setVincLoading(false); }
  }

  const subcategoriasFiltradas = subcategorias.filter(s => s.categoria_codigo === form.categoria_codigo);
  const docsDisponiveis = todosDocs.filter(d =>
    documento && d.id !== documento.id && !vinculados.some(v => v.id === d.id)
  );

  const ehDocx = !!arquivo && /\.(docx)$/i.test(arquivo.name);

  async function salvar() {
    if (!form.titulo.trim()) { message.warning('Título obrigatório'); return; }
    if (!form.categoria_codigo) { message.warning('Categoria obrigatória'); return; }
    if (!editando && !arquivo) { message.warning('Selecione um arquivo pra upload'); return; }

    setSalvando(true);
    try {
      const token = await getToken();

      if (editando) {
        // Edição: só metadata (não troca arquivo)
        setProgressoMsg('Salvando alterações...');
        await axios.post(`${API_URL}/comercial/documentos`,
          { action: 'atualizar', id: documento!.id, ...form,
            subcategoria_codigo: form.subcategoria_codigo || null,
            data_emissao: form.data_emissao || null,
            data_validade: form.data_validade || null,
            eh_template: ehTemplate,
            visivel_comercial_override: visComercial,
            template_campos: [], // legado — IA preenche sem placeholders
          },
          { headers: { Authorization: token } });
        message.success('Documento atualizado.');
        onSaved();
        return;
      }

      // Criação: upload → criar (com sumarização IA)
      setProgressoMsg('Preparando upload...');
      const respUrl = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'upload_url',
          filename: arquivo!.name,
          content_type: arquivo!.type || 'application/octet-stream',
          categoria_codigo: form.categoria_codigo,
        },
        { headers: { Authorization: token } });
      const urlData = respUrl.data.body ? JSON.parse(respUrl.data.body) : respUrl.data;
      const { upload_url, s3_key } = urlData;

      setProgressoMsg('Enviando arquivo pro servidor...');
      await axios.put(upload_url, arquivo, {
        headers: { 'Content-Type': arquivo!.type || 'application/octet-stream' },
      });

      setProgressoMsg(ehTemplate ? 'Salvando modelo...' : 'Lendo e resumindo com IA (5-15s)...');
      const respCriar = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'criar',
          s3_key,
          filename_original: arquivo!.name,
          ...form,
          subcategoria_codigo: form.subcategoria_codigo || null,
          data_emissao: form.data_emissao || null,
          data_validade: form.data_validade || null,
          eh_template: ehTemplate,
          visivel_comercial_override: visComercial,
          template_campos: [], // legado — IA preenche sem placeholders
          esperado_id: paraEsperado?.id ?? null,
        },
        { headers: { Authorization: token } });
      const criadoData = respCriar.data.body ? JSON.parse(respCriar.data.body) : respCriar.data;

      if (criadoData.sumarizacao_ok) {
        setSumarioMd(criadoData.documento?.sumario_md || null);
        message.success('Documento criado e sumarizado pela IA.');
      } else {
        message.warning('Documento criado, mas a sumarização falhou. Você pode editar e regerar depois.');
      }
      onSaved();
    } catch (e: any) {
      const detalhe = e.response?.data?.error || e.response?.data?.body || e.message;
      message.error('Erro: ' + (typeof detalhe === 'string' ? detalhe : 'falha no upload'));
    } finally {
      setSalvando(false);
      setProgressoMsg('');
    }
  }

  // L10 (2026-06-02): troca o arquivo do doc atual (mantem id+codigo+vinculos).
  async function trocarArquivo() {
    if (!documento) return;
    if (!trocaArquivo) { message.warning('Selecione o novo arquivo'); return; }
    setTrocando(true);
    try {
      const token = await getToken();
      // 1) presigned upload (reusa endpoint normal)
      const respUrl = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'upload_url',
          filename: trocaArquivo.name,
          content_type: trocaArquivo.type || 'application/octet-stream',
          categoria_codigo: documento.categoria_codigo,
        }, { headers: { Authorization: token } });
      const dataUrl = respUrl.data?.body ? JSON.parse(respUrl.data.body) : respUrl.data;
      // 2) PUT direto S3
      await axios.put(dataUrl.upload_url, trocaArquivo, {
        headers: { 'Content-Type': trocaArquivo.type || 'application/octet-stream' },
        timeout: 300000,
      });
      // 3) atualizar com s3_key novo + datas opcionais
      await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'atualizar', id: documento.id,
          s3_key: dataUrl.s3_key,
          filename_original: trocaArquivo.name,
          ...(trocaDataEmissao ? { data_emissao: trocaDataEmissao } : {}),
          ...(trocaDataValidade ? { data_validade: trocaDataValidade } : {}),
        }, { headers: { Authorization: token } });
      message.success('Arquivo substituído. Sumário antigo limpo — re-sumarize se quiser.');
      setTrocarOpen(false);
      setTrocaArquivo(null);
      setTrocaDataEmissao('');
      setTrocaDataValidade('');
      onSaved();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setTrocando(false); }
  }

  return (
    <Modal open onCancel={onClose} onOk={somenteLeitura ? undefined : salvar} confirmLoading={salvando}
      // Regra do CLAUDE.md: modal não obriga a rolar tendo tela sobrando. Com
      // vínculos + sumário + anexo, 760px fixos rolavam em qualquer monitor.
      width="94vw" style={{ maxWidth: 980, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      footer={somenteLeitura ? [<Button key="fechar" onClick={onClose}>Fechar</Button>] : undefined}
      destroyOnClose
      title={editando
        ? (
          <Space size={8} wrap>
            <span>{somenteLeitura ? 'Documento' : 'Editar documento'}</span>
            {documento?.codigo && <Text code style={{ fontSize: 11 }}>{documento.codigo}</Text>}
            {/* L10 (2026-06-02): trocar o arquivo sem criar novo cadastro */}
            {!somenteLeitura && (
              <Button size="small" icon={<UploadOutlined />} onClick={() => setTrocarOpen(true)}
                title="Substituir o arquivo deste documento (mantém código + vínculos). Útil pra renovação de CND, atestado revisado, etc.">
                Trocar arquivo
              </Button>
            )}
          </Space>
        )
        : (paraEsperado ? `Anexar: ${paraEsperado.nome}` : 'Novo documento')}
      okText={editando ? 'Salvar' : 'Enviar e analisar'}
      cancelText="Cancelar"
    >
      {!editando && paraEsperado && (
        <Alert type="success" showIcon style={{ marginBottom: 14 }}
          message={`Este documento vai fechar o item "${paraEsperado.nome}" do checklist`}
          description="Categoria e subcategoria já vêm do item. O vínculo fica gravado no documento — mudar a categoria depois não desfaz o vínculo." />
      )}

      {salvando && progressoMsg && (
        <Alert
          message={<Spin size="small" />} description={progressoMsg}
          type="info" style={{ marginBottom: 12 }}
        />
      )}

      {/* L10 (2026-06-02): destaque visivel no topo do modal pra trocar o arquivo.
          O botao do titulo pode ficar cortado em telas pequenas — esse aqui
          fica garantido no corpo. */}
      {editando && somenteLeitura && (
        <Alert type="info" showIcon style={{ marginBottom: 14 }}
          message="Consulta ao acervo da empresa"
          description={<>Este documento é mantido pelo <b>Administrativo</b>. Aqui você consulta e baixa; para atualizar, fale com quem cuida do acervo.</>} />
      )}

      {editando && !somenteLeitura && (
        <Alert type="info" showIcon
          style={{ marginBottom: 14 }}
          message={
            <Space size={8} wrap>
              <span>
                <strong>Arquivo atual:</strong>{' '}
                {documento?.arquivo_nome_original || '(sem nome)'}
              </span>
            </Space>
          }
          description="Pra renovação de certidão, atestado revisado, declaração atualizada: substitua o arquivo aqui em vez de criar um novo cadastro. Mantém o mesmo código e todos os vínculos em checklists."
          action={
            <Button type="primary" icon={<UploadOutlined />}
              onClick={() => setTrocarOpen(true)}>
              Trocar arquivo
            </Button>
          }
        />
      )}

      <Form layout="vertical" size="small">
        <Row gutter={10}>
          <Col span={12}>
            <Form.Item label="Categoria" required>
              <Select value={form.categoria_codigo}
                onChange={(v) => { set('categoria_codigo', v); set('subcategoria_codigo', ''); }}
                options={categorias.map(c => ({ label: c.nome, value: c.codigo }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Subcategoria">
              <Select value={form.subcategoria_codigo || undefined}
                onChange={(v) => set('subcategoria_codigo', v || '')}
                allowClear
                placeholder={subcategoriasFiltradas.length ? 'Opcional — escolha o tipo específico' : 'Categoria sem subcategorias'}
                disabled={!subcategoriasFiltradas.length}
                options={subcategoriasFiltradas.map(s => ({ label: s.nome, value: s.codigo }))} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Número do documento">
              <Input value={form.numero_documento}
                onChange={e => set('numero_documento', e.target.value)}
                placeholder="CAT 12345, CND 999-XYZ..." />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Título" required>
              <Input value={form.titulo} onChange={e => set('titulo', e.target.value)}
                placeholder='Ex.: "CAT 12345 — Levantamento batimétrico Rio Taquari"' />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Emitente">
              <Input value={form.emitente} onChange={e => set('emitente', e.target.value)}
                placeholder="Ex.: CREA-RJ, Receita Federal" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Descrição">
              <Input value={form.descricao} onChange={e => set('descricao', e.target.value)}
                placeholder="Observação curta (opcional)" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Data de emissão">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={form.data_emissao ? dayjs(form.data_emissao) : null}
                onChange={(d) => set('data_emissao', d ? d.format('YYYY-MM-DD') : '')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Validade (vazio = permanente)">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={form.data_validade ? dayjs(form.data_validade) : null}
                onChange={(d) => set('data_validade', d ? d.format('YYYY-MM-DD') : '')} />
            </Form.Item>
          </Col>

          {!editando && (
            <Col span={24}>
              <Form.Item label="Arquivo" required>
                <Upload beforeUpload={(file) => { setArquivo(file); return false; }}
                  maxCount={1} onRemove={() => setArquivo(null)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                  fileList={arquivo ? [{ uid: '1', name: arquivo.name, status: 'done' } as any] : []}>
                  <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
                </Upload>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Aceita PDF, Word (.docx), Excel (.xlsx), imagens e texto.
                  Sumário automático por IA só pra PDF e texto (~5-15s) — outros formatos você descreve manualmente.
                </Text>
              </Form.Item>
            </Col>
          )}

          {editando && (
            <Col span={24}>
              <Tag icon={<FileTextOutlined />} color="default" style={{ fontSize: 11 }}>
                {documento!.arquivo_nome_original || 'arquivo anexado'}
              </Tag>
              {/* o texto antigo dizia "exclua este documento e crie outro" —
                  contradizia o botão "Trocar arquivo" que existe desde a L10 */}
              <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                Use <strong>Trocar arquivo</strong> para substituir mantendo código e vínculos.
              </Text>
            </Col>
          )}

          {/* Quem enxerga este documento fora do Administrativo (21/08) */}
          <Col span={24}>
            <Divider style={{ margin: '8px 0' }} />
            <Text strong style={{ fontSize: 13 }}>Disponível para o Comercial?</Text>
            <div style={{ marginTop: 6 }}>
              <Radio.Group value={visComercial === null ? 'herda' : (visComercial ? 'sim' : 'nao')}
                onChange={(e) => setVisComercial(
                  e.target.value === 'herda' ? null : e.target.value === 'sim')}
                size="small" optionType="button" buttonStyle="solid">
                <Radio.Button value="herda">Como a categoria</Radio.Button>
                <Radio.Button value="sim">Sim, mostrar</Radio.Button>
                <Radio.Button value="nao">Não mostrar</Radio.Button>
              </Radio.Group>
            </div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
              Decide se o documento aparece na consulta do Comercial (aba de Licitações)
              e no que o Dwight lê para montar checklist de habilitação.
              {' '}<strong>Não é sigilo</strong> — quem tem acesso ao Administrativo continua
              vendo tudo. Serve para não poluir a consulta de habilitação com papel que não
              serve a edital.
            </Text>
          </Col>

          {/* F8 — Modelo editavel pela IA (sem placeholders manuais) */}
          {(ehDocx || (editando && documento?.arquivo_nome_original?.endsWith('.docx'))) && (
            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
              <Space align="start">
                <Switch checked={ehTemplate} onChange={setEhTemplate} size="small" />
                <div>
                  <Text strong>É modelo editável pela IA?</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Quando vinculado a um item do checklist, aparece o botão "Gerar com IA" — a IA lê o modelo,
                      preenche automaticamente com dados da empresa + dados da licitação, e devolve o .docx pronto.
                      Não precisa colocar placeholders no Word.
                    </Text>
                  </div>
                </div>
              </Space>
            </Col>
          )}

          {sumarioMd && (
            <Col span={24}>
              <Form.Item label="Sumário gerado pela IA">
                <div style={{
                  background: '#f6f8fa', border: '1px solid #d0d7de', borderRadius: 6,
                  padding: 10, fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 220,
                  overflow: 'auto',
                }}>{sumarioMd}</div>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {editando && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div>
            <Text strong style={{ fontSize: 13 }}>
              <LinkOutlined /> Vínculos com outros documentos
            </Text>
            <div style={{ fontSize: 10, color: '#888', marginTop: 2, marginBottom: 8 }}>
              Ex.: atestado técnico ↔ CAT correspondente. Vínculo é bidirecional.
            </div>

            {vinculados.length === 0 && (
              <Text type="secondary" style={{ fontSize: 11 }}>Nenhum vínculo cadastrado.</Text>
            )}
            {vinculados.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {vinculados.map(v => (
                  <div key={v.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                    background: '#f6f8fa', borderRadius: 4, marginBottom: 4, fontSize: 11,
                  }}>
                    <Tag color="blue" style={{ fontSize: 9, margin: 0 }}>{v.categoria_nome}</Tag>
                    {v.subcategoria_nome && <Tag style={{ fontSize: 9, margin: 0 }}>{v.subcategoria_nome}</Tag>}
                    <Text style={{ flex: 1, fontSize: 11 }}>{v.titulo}</Text>
                    {v.tipo_vinculo && (
                      <Tag color="purple" style={{ fontSize: 9, margin: 0 }}>
                        {TIPO_VINCULO_LABEL[v.tipo_vinculo] || v.tipo_vinculo}
                      </Tag>
                    )}
                    {!somenteLeitura && (
                      <Popconfirm title="Remover vínculo?" onConfirm={() => removerVinculo(v.id)}
                        okText="Remover" cancelText="Cancelar">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} loading={vincLoading} />
                      </Popconfirm>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!somenteLeitura && (
            <Space.Compact style={{ width: '100%' }}>
              <Select value={novoVincDocId || undefined} onChange={(v) => setNovoVincDocId(v || null)}
                showSearch optionFilterProp="label" allowClear
                placeholder="Buscar documento pra vincular..."
                style={{ flex: 1 }} size="small"
                options={docsDisponiveis.map(d => ({
                  label: `[${d.categoria_nome}${d.subcategoria_nome ? '/' + d.subcategoria_nome : ''}] ${d.titulo}`,
                  value: d.id,
                }))} />
              <Select value={novoVincTipo} onChange={(v) => setNovoVincTipo(v)}
                size="small" style={{ width: 200 }}
                options={Object.entries(TIPO_VINCULO_LABEL).map(([k, v]) => ({ label: v, value: k }))} />
              <Button type="primary" size="small" icon={<PlusOutlined />}
                onClick={adicionarVinculo} loading={vincLoading}
                disabled={!novoVincDocId}>Vincular</Button>
            </Space.Compact>
            )}
          </div>
        </>
      )}

      {/* L10 (2026-06-02) — Modal aninhado pra trocar arquivo do doc */}
      <Modal open={trocarOpen}
        onCancel={() => !trocando && setTrocarOpen(false)}
        onOk={trocarArquivo} confirmLoading={trocando}
        okText="Substituir arquivo" cancelText="Cancelar"
        okButtonProps={{ disabled: !trocaArquivo }}
        title="Trocar o arquivo deste documento" width={520} destroyOnClose>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Substitui o arquivo sem criar novo cadastro"
          description="O código e os vínculos (checklists, etc) ficam intactos. O sumário IA é zerado — você re-sumarize se precisar. O arquivo antigo continua no S3 mas o doc passa a apontar pro novo." />
        <Form layout="vertical" size="small">
          <Form.Item label="Novo arquivo" required>
            <Upload beforeUpload={(file) => { setTrocaArquivo(file as File); return false; }}
              maxCount={1} onRemove={() => setTrocaArquivo(null)}
              fileList={trocaArquivo
                ? [{ uid: '1', name: trocaArquivo.name, status: 'done' } as any]
                : []}>
              <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
            </Upload>
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Nova data de emissão (opcional)">
                <DatePicker style={{ width: '100%' }}
                  value={trocaDataEmissao ? dayjs(trocaDataEmissao) : null}
                  onChange={(d) => setTrocaDataEmissao(d ? d.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Nova data de validade (opcional)">
                <DatePicker style={{ width: '100%' }}
                  value={trocaDataValidade ? dayjs(trocaDataValidade) : null}
                  onChange={(d) => setTrocaDataValidade(d ? d.format('YYYY-MM-DD') : '')}
                  format="DD/MM/YYYY" allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Modal>
  );
}
